/* THE EQUIPMENT LIST — the rules behind the Library's Equipment page and the builder's picker
 * (his direction, 2026-09-23, point 6). Pure, so a test drives every one; the screen only draws.
 *
 * THE MODEL, stated once: the app ships ONE vocabulary of equipment (`EQUIPMENT_GROUPS`, generated
 * from the phone's enum). A coach may HIDE any of it from their own builder, RENAME it, or give it
 * another ICON — each a row in `coach_equipment` for that built-in key — and may make pieces of
 * their OWN (`custom_<slug>_<id>`, the row IS the piece). Nothing here rewrites an exercise: an
 * exercise stores KEYS, and a rename or a hide changes a drawing, never a prescription.
 *
 * 🔴 "BACK TO DEFAULTS" IS DELETING THE ROW (his decision, 2026-09-23 ~09:45: *"we need a back to
 * defaults option somewhere so he can go back safely … So they don't contact us each time they're
 * stuck"*). No row means "as the app ships it", so restoring can never half-work: per piece, and all
 * built-ins at once, and neither ever touches the coach's OWN pieces.
 *
 * ⚠️ THE PHONE READS THE SAME ROWS (`CoachEquipmentDirectory.swift`), and these contracts are shared
 * with it word for word: the custom key's shape, the slug fallback, and the three shapes of
 * `icon_path` (`glyph:<key>` · `<…>-mask.png` · any other upload). */
import { EQUIPMENT_GROUPS, EQUIPMENT_LABEL } from "./equipment";
import { vocabularyLabel } from "./vocabulary";
import { SUPABASE_URL } from "./supabase";

/** One `coach_equipment` row, as the web reads it. */
export type EquipmentSetting = {
  key: string;
  hidden: boolean;
  name?: string | null;
  icon_path?: string | null;
};

/** A piece of equipment as the page draws it. */
export type EquipmentItem = {
  key: string;
  /** What the coach sees — his name where he gave one. */
  label: string;
  /** The app's own name for a built-in; the slug's for a custom piece. */
  originalLabel: string;
  hidden: boolean;
  usedBy: number;
  custom: boolean;
  /** A built-in this coach changed in any way — the only ones "Restore default" applies to. */
  changed: boolean;
  iconPath: string | null;
};

/* ── the custom key — the ONE definition, mirrored by `CustomEquipmentKey` on the phone ── */

export const CUSTOM_PREFIX = "custom_";

export function isCustomKey(key: string): boolean {
  return key.startsWith(CUSTOM_PREFIX);
}

/** Six lower-case letters or digits. */
export function randomId6(random: () => number = Math.random): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  for (let i = 0; i < 6; i++) out += alphabet[Math.floor(random() * alphabet.length) % alphabet.length];
  return out;
}

/** `Wooden Rings!` → `custom_wooden_rings_<id>`. The slug is what the piece is called at CREATION,
 *  so a phone that cannot read the row still says roughly what it is. Fits the table's CHECK
 *  (`^[a-z0-9_]{1,64}$`) whatever the name holds — accents, emoji and all are dropped, not refused. */
export function customKey(name: string, id: string): string {
  // Accents are TAKEN OFF, never turned into a separator: NFKD splits "ć" into "c" + a combining
  // mark, and without this the mark became "_" — "Ćwiczenie" slugged as `c_wiczenie`.
  const slug = name.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const room = 64 - CUSTOM_PREFIX.length - 1 - id.length;
  const cut = slug.slice(0, room).replace(/_+$/g, "");
  return cut === "" ? `${CUSTOM_PREFIX}${id}` : `${CUSTOM_PREFIX}${cut}_${id}`;
}

/** The phone's `CustomEquipmentKey.fallbackName`, word for word. */
export function customFallbackName(key: string): string | null {
  if (!isCustomKey(key)) return null;
  const isId = (s: string) => /^[a-z0-9]{6}$/.test(s);
  const parts = key.slice(CUSTOM_PREFIX.length).split("_").filter((p) => p !== "");
  if (parts.length >= 2 && isId(parts[parts.length - 1]!)) parts.pop();
  if (parts.length === 0 || (parts.length === 1 && isId(parts[0]!) && /\d/.test(parts[0]!))) return null;
  const words = parts.join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const UNNAMED_CUSTOM = "Coach's equipment";

/** The typed name when there is one worth printing. */
function typedName(s: EquipmentSetting | undefined): string | null {
  const n = s?.name?.trim();
  return n === undefined || n === "" ? null : n;
}

/** 🔴 THE ONE ANSWER TO "WHAT IS THIS KEY CALLED" on the web — the phone's `displayName(for:)`. */
export function equipmentName(key: string, settings: readonly EquipmentSetting[]): string {
  const typed = typedName(settings.find((s) => s.key === key));
  if (typed !== null) return typed;
  if (isCustomKey(key)) return customFallbackName(key) ?? UNNAMED_CUSTOM;
  return vocabularyLabel(key);
}

/** What the app itself calls the piece — the name "Restore default" brings back. */
export function originalName(key: string): string {
  return isCustomKey(key) ? customFallbackName(key) ?? UNNAMED_CUSTOM : vocabularyLabel(key);
}

/* ── icons ── */

export const ICON_BUCKET = "equipment-icons";
export const GLYPH_PREFIX = "glyph:";

/** `glyph:pull_up_bar` → `pull_up_bar`; null for an upload (or nothing). */
export function glyphOf(iconPath: string | null | undefined): string | null {
  if (iconPath === null || iconPath === undefined || !iconPath.startsWith(GLYPH_PREFIX)) return null;
  const key = iconPath.slice(GLYPH_PREFIX.length);
  return EQUIPMENT_LABEL[key] === undefined ? null : key;
}

export function iconIsMask(iconPath: string): boolean {
  return iconPath.endsWith("-mask.png");
}

/** The public URL of an UPLOADED icon; null for a `glyph:` reference or none. */
export function iconUrl(iconPath: string | null | undefined): string | null {
  if (iconPath === null || iconPath === undefined || iconPath === "" || iconPath.startsWith(GLYPH_PREFIX)) return null;
  return `${SUPABASE_URL}/storage/v1/object/public/${ICON_BUCKET}/${iconPath.split("/").map(encodeURIComponent).join("/")}`;
}

/** 🔴 THE ONE PLACE AN ICON'S PATH IS MADE. The owner folder is the coach's id LOWER-CASED — the
 *  bucket's policy compares it with `auth.uid()::text`, which Postgres renders lower-case (the
 *  42501 of 2026-09-12, five builders, one right). A fresh stamp every time: the bucket is public and
 *  a replaced object is served stale from the CDN. */
export function iconObjectPath(coachId: string, key: string, isMask: boolean, stamp: number): string {
  return `${coachId.toLowerCase()}/${key}-${String(stamp)}${isMask ? "-mask" : ""}.png`;
}

/* ── reading the rows ── */

/** The keys this coach has hidden. */
export function hiddenKeys(settings: readonly EquipmentSetting[]): Set<string> {
  return new Set(settings.filter((s) => s.hidden).map((s) => s.key));
}

/** How many of the coach's exercises name each key — read off the exercises, never stored. */
export function usageCounts(exerciseEquipment: readonly (readonly string[] | null)[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const list of exerciseEquipment) {
    for (const k of new Set(list ?? [])) out.set(k, (out.get(k) ?? 0) + 1);
  }
  return out;
}

/** A built-in row exists — hidden, renamed or re-iconed. Every such row is a change, since a row
 *  saying nothing would be "shown as shipped", which is what NO row already says. */
export function isChangedBuiltIn(s: EquipmentSetting): boolean {
  return !isCustomKey(s.key);
}

/** The built-in keys "Restore all defaults" deletes. NEVER a custom key. */
export function builtInsToRestore(settings: readonly EquipmentSetting[]): string[] {
  return settings.filter(isChangedBuiltIn).map((s) => s.key);
}

function itemFor(key: string, settings: readonly EquipmentSetting[], usage: ReadonlyMap<string, number>): EquipmentItem {
  const row = settings.find((s) => s.key === key);
  return {
    key,
    label: equipmentName(key, settings),
    originalLabel: originalName(key),
    hidden: row?.hidden ?? false,
    usedBy: usage.get(key) ?? 0,
    custom: isCustomKey(key),
    changed: row !== undefined && isChangedBuiltIn(row),
    iconPath: row?.icon_path ?? null,
  };
}

export type EquipmentFilter = "all" | "changed";

/** The page's sections: the coach's OWN pieces first, then the app's groups in the app's order,
 *  filtered by the search (his names AND the app's, so a renamed piece is found by either). The
 *  `changed` view is the "Defaults" place: only the built-ins he altered, each with its restore. */
export function equipmentPageSections(settings: readonly EquipmentSetting[], usage: ReadonlyMap<string, number>,
                                      query: string, filter: EquipmentFilter = "all"): { name: string; items: EquipmentItem[] }[] {
  const q = query.trim().toLowerCase();
  const matches = (it: EquipmentItem) => q === ""
    || it.label.toLowerCase().includes(q) || it.originalLabel.toLowerCase().includes(q);
  const own = settings.filter((s) => isCustomKey(s.key)).map((s) => itemFor(s.key, settings, usage))
    .sort((a, b) => a.label.localeCompare(b.label));
  const sections = [
    { name: "Your equipment", items: filter === "changed" ? [] : own.filter(matches) },
    ...EQUIPMENT_GROUPS.map((g) => ({
      name: g.name,
      items: g.keys.map((k) => itemFor(k, settings, usage))
        .filter((it) => (filter === "all" || it.changed) && matches(it)),
    })),
  ];
  return sections.filter((s) => s.items.length > 0);
}

/** What the BUILDER offers: every key the app ships minus what this coach hid — EXCEPT a hidden
 *  piece the exercise already holds, which stays visible (as a chosen pill) so opening an old row
 *  never silently drops what it names. */
export function offeredInBuilder(key: string, hidden: ReadonlySet<string>, held: readonly string[]): boolean {
  return !hidden.has(key) || held.includes(key);
}

/** The coach's own pieces the builder offers (not hidden), plus any custom key the exercise holds. */
export function customOfferedInBuilder(settings: readonly EquipmentSetting[], held: readonly string[]): string[] {
  const own = settings.filter((s) => isCustomKey(s.key) && !s.hidden).map((s) => s.key);
  const stragglers = held.filter((k) => isCustomKey(k) && !own.includes(k));
  return [...own, ...stragglers].sort((a, b) => equipmentName(a, settings).localeCompare(equipmentName(b, settings)));
}

/** Every key the app ships — the page's denominator. */
export function allEquipmentKeys(): string[] {
  return EQUIPMENT_GROUPS.flatMap((g) => g.keys).filter((k) => EQUIPMENT_LABEL[k] !== undefined);
}

/** A copy's name: `Barbell` → `Barbell copy`, and never one the coach already has. */
export function copyName(label: string, taken: ReadonlySet<string>): string {
  const base = `${label} copy`;
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; n < 100; n++) {
    const candidate = `${base} ${String(n)}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `${base} ${String(Date.now())}`;
}

/* 🔴 CREATING A PIECE OF HIS OWN WAITS FOR THE PHONE (W62 step C, the order his decision set). A
 * client sees equipment on every coached workout, and a phone older than the build that reads
 * `coach_equipment` would print a custom key as code. Flip this ONLY once that build is the public
 * TestFlight build (`tools/testflight/verify-register.sh`), in the same commit that says so. Renaming,
 * re-iconing with one of OUR drawings, hiding and restoring need nothing new on the phone. */
// ✅ OPENED 2026-09-23: build 89 (cut at f08a3d10, the first that reads `coach_equipment`) is the ONLY
// live iOS build — `verify-register.sh 89`: "iOS live = ['89']", build 88 expired by `promote.sh`.
export const CUSTOM_EQUIPMENT_OPEN = true;

/* 🔴 UPLOADING AN ICON WAITS FOR THE `equipment-icons` BUCKET (W62 step C(c), rehearsed and parked in
 * `tools/coach-equipment-client-read-20260923/`). Until it is live an upload has nowhere to go, so
 * the control is not drawn at all rather than drawn and refused. Choosing one of OUR drawings
 * (`glyph:<key>`) needs no bucket and is open now. */
// ✅ OPENED 2026-09-23: `20260923160000` pushed ALONE with its sibling (dry run named exactly the two),
// read back live (bucket public · 262144 · image/png; 4 owner policies), the rehearsal re-run against
// the LIVE objects 8/8. Account deletion sweeps the bucket (`delete-account` OWNED_BUCKETS, deployed,
// `verify-sweep.sh` VERIFIED).
export const ICON_UPLOAD_OPEN = true;

/* ── the one write rule ── */

export const NAME_MAX = 60;

export type SettingWrite =
  | { kind: "delete"; key: string }
  | { kind: "upsert"; row: { key: string; name: string | null; icon_path: string | null; hidden: boolean } }
  | { kind: "refuse"; reason: string };

/** What saving the editor writes. 🔴 A BUILT-IN EDITED BACK TO HOW THE APP SHIPS IT LEAVES NO ROW:
 *  its name equal to the app's (or blank) is stored as null, its own drawing as null, and a row that
 *  would then say nothing is DELETED — so "no row = the default" stays true by construction, and
 *  "Changed" never lists a piece that is not. A custom piece must be called something (the table's
 *  CHECK says so too; this says it first, in words). */
export function settingWrite(key: string, typed: string, iconPath: string | null, hidden: boolean): SettingWrite {
  const name = typed.trim();
  if (name.length > NAME_MAX) return { kind: "refuse", reason: `Keep the name under ${String(NAME_MAX)} characters.` };
  if (isCustomKey(key)) {
    if (name === "") return { kind: "refuse", reason: "Give it a name." };
    return { kind: "upsert", row: { key, name, icon_path: iconPath, hidden } };
  }
  const storedName = name === "" || name === originalName(key) ? null : name;
  const storedIcon = iconPath === `${GLYPH_PREFIX}${key}` ? null : iconPath;
  if (storedName === null && storedIcon === null && !hidden) return { kind: "delete", key };
  return { kind: "upsert", row: { key, name: storedName, icon_path: storedIcon, hidden } };
}
